import styled from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, MOBILE, PLAYABLE_PICKER, PLAYABLE_PREVIEW, SPACE, STATUS_COLORS } from '@/nova/theme/tokens';

/* The mini-game and HTML5 banner composer: the Image Banners composer's frame (header with name + version; left, the
   Art Style page's ingredient sections over a pinned Generate; right, the output over a pinned export), so the ad
   editors read as one system. Its own pieces: the template picker, the sandboxed preview stage, the schema-driven
   settings form, the size cards, the art strip and the export. */

export {
  ArtStyleBody,
  BudgetOk,
  BudgetOver,
  CopyHeadline,
  CopySub,
  FileHead,
  FileList,
  FileMeta,
  FileName,
  FileRow,
  FootText,
  Guide,
  GuideIcon,
  GuideText,
  Header,
  HeaderLeft,
  Hint,
  KitOutput,
  KitRail,
  LockNote,
  LumioBtn,
  OutputFoot,
  OutputScroll,
  ProgressFill,
  ProgressTrack,
  RailFoot,
  RailScroll,
  RailStatus,
  ReviewBadge,
  Section,
  SectionHead,
  SectionTitle,
  Spinner,
  Split,
  SubjectCard,
  SubjectCopy,
  SubjectThumb,
  Tile as SizeCard,
  TileCaption as SizeCardFoot,
  TileSize as SizeCardLabel,
  TitleEditIcon,
  TitleInput,
  TitleWrap,
  ToggleLabel,
  ToggleRow,
  Violation,
} from '../BannerKitComposer/BannerKitComposer.styles';

/* The hosted embed's iframe URL reuses the tracking link's read-only URL + contained copy control. */
export { CopyBtn, CopyField, CopyUrl } from '../TrackingLink/TrackingLink.styles';

/* ── Template picker ── */

export const PickerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

export const PickerHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SPACE[12]};
`;

export const PickerIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
  min-width: 0;
  flex: 1 1 280px;
`;

export const PickerActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
`;

/** The picker grid: cards at least `$minCard` px wide (a game card's 200 px by default). `$fit` lets a short list
 *  share the whole row (auto-fit collapses the empty tracks) instead of leaving room for cards that are not there. */
export const TemplateGrid = styled.div<{ $minCard?: number; $fit?: boolean }>`
  display: grid;
  grid-template-columns: repeat(
    ${(p) => (p.$fit ? 'auto-fit' : 'auto-fill')},
    minmax(${(p) => Math.max(200, p.$minCard ?? 200)}px, 1fr)
  );
  gap: ${SPACE[16]};
`;

export const TemplateCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: ${SPACE[12]};
  border-radius: 14px;
  border: 1px solid ${(p) => (p.$selected ? COLORS.text : COLORS.hairline)};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.text};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/** The card's live preview slot: the template's demo at 320x480, scaled down; inert (a click picks the card). */
/** A picker card plus its replay control (a sibling, so no button sits inside the card button). */
export const TemplateCardWrap = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  & > button:first-child {
    flex: 1 1 auto;
  }
`;

/** Replays the hovered card's playthrough; shown while the card is hovered or keyboard-focused. */
export const CardReplay = styled.button`
  position: absolute;
  z-index: 1;
  top: ${SPACE[16]};
  right: ${SPACE[16]};
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  padding: ${SPACE[4]} ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  opacity: 0;
  transition: opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  ${TemplateCardWrap}:hover &,
  ${TemplateCardWrap}:focus-within &,
  &:focus-visible {
    opacity: 1;
  }
  &:disabled {
    display: none;
  }
`;

export const TemplatePreviewSlot = styled.div<{ $w?: number; $h?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  width: ${(p) => p.$w ?? 160}px;
  height: ${(p) => p.$h ?? 240}px;
  max-width: 100%;
  border-radius: 10px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
  pointer-events: none;
`;

export const TemplateName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 700;
  color: ${COLORS.text};
`;

export const TemplateText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.45;
  color: ${COLORS.textFaint};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const SelectedMark = styled(Text).attrs({ tag: 'span' as const })`
  align-self: flex-start;
  padding: 1px ${SPACE[8]};
  border-radius: 999px;
  border: 1px solid ${STATUS_COLORS.positive};
  color: ${STATUS_COLORS.positive};
  font-size: 11px;
  font-weight: 600;
`;

/* ── Current template (rail) ── */

export const TemplateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACE[12]};
  padding: ${SPACE[8]} ${SPACE[12]};
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
`;

/* ── Preview ── */

export const PreviewStage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[12]};
  padding: ${SPACE[16]};
  border-radius: 14px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${SPACE[8]};
`;

/** The preview box at the frame's scaled size (the iframe inside keeps its real CSS pixels). */
export const PreviewBox = styled.div<{ $w: number; $h: number }>`
  position: relative;
  /* Keeps the frame layers' stacking inside the box. */
  isolation: isolate;
  flex: 0 0 auto;
  width: ${(p) => Math.round(p.$w)}px;
  height: ${(p) => Math.round(p.$h)}px;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
`;

/** One document of a preview frame: hidden while it loads, then faded in over the one on screen (later layers paint
 *  above earlier ones), which is dropped once the fade is over. */
export const FrameLayer = styled.div<{ $shown: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transition: opacity ${PLAYABLE_PREVIEW.SWAP_MS}ms ${ANIM.EASE};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The sandboxed preview document, at the format's real size, scaled into its box from the top-left corner. */
export const PreviewIframe = styled.iframe<{ $scale: number }>`
  position: absolute;
  top: 0;
  left: 0;
  border: 0;
  display: block;
  transform: scale(${(p) => p.$scale});
  transform-origin: 0 0;
`;

/** A picker card's demo: its resting frame, with (a runtime without the control channel only) a playthrough frame
 *  stacked over it (see `card-demo-layers`), at the frame's scaled size. */
export const DemoStackBox = styled.div<{ $w: number; $h: number }>`
  position: relative;
  /* Keeps the layers' z-index inside the demo, so the card's Replay control always paints above it. */
  isolation: isolate;
  flex: 0 0 auto;
  width: ${(p) => Math.round(p.$w)}px;
  height: ${(p) => Math.round(p.$h)}px;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: ${COLORS.surfaceInset};
`;

/** One frame of a card's demo: the resting frame, or (no control channel) a playthrough hidden while its document
 *  loads, then faded in over the loop; fading out on leave shows the loop still running underneath. */
export const DemoLayer = styled.div<{ $shown: boolean; $top: boolean }>`
  position: absolute;
  inset: 0;
  z-index: ${(p) => (p.$top ? 2 : 1)};
  opacity: ${(p) => (p.$shown ? 1 : 0)};
  transition: opacity ${PLAYABLE_PICKER.CROSSFADE_MS}ms ${ANIM.EASE};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const PreviewNote = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[16]};
  min-height: 160px;
  text-align: center;
`;

export const NoteText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.45;
  color: ${COLORS.textFaint};
`;

/* ── Art strip ── */

export const ArtStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[12]};
`;

export const ArtItem = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACE[4]};
`;

export const ArtThumb = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  display: block;
`;

export const ArtLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

/* ── Settings form ── */

export const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
`;

export const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

export const FieldLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const FieldError = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${STATUS_COLORS.negative};
`;

export const SwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${SPACE[12]};

  ${MOBILE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const ColorRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${SPACE[8]};

  & > :last-child {
    flex: 1 1 auto;
    min-width: 0;
  }
`;

/** The native colour picker, drawn as a rounded swatch. */
export const ColorInput = styled.input.attrs({ type: 'color' })`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 10px;
  border: 1px solid ${COLORS.panelBorder};
  background: transparent;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: ${SPACE[4]};
  }
  &::-webkit-color-swatch {
    border: 0;
    border-radius: 6px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ArrayList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const ArrayItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]};
  border-radius: 10px;
  border: 1px solid ${COLORS.hairline};
`;

export const ArrayIndex = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  min-width: 24px;
  padding-top: ${SPACE[8]};
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
`;

export const ArrayItemBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const ArrayActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

export const CheckRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACE[8]} ${SPACE[16]};
`;

/* ── Export ── */

export const ExportPanel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  padding: ${SPACE[16]};
  border-radius: 14px;
  border: 1px solid ${COLORS.hairline};
`;

/** The export's profile picker in the output's foot, beside Export. */
export const ExportPick = styled.div`
  flex: 0 1 260px;
  min-width: 0;
`;

/* ── Hosted embed ── */

/** The publish controls: the format picker grows, the button keeps its size. */
export const EmbedControls = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: ${SPACE[8]};

  & > :first-child {
    flex: 1 1 180px;
    min-width: 0;
  }
`;

/** The iframe snippet: read-only, selectable, monospaced (pasted as is into a page). */
export const EmbedCode = styled.textarea`
  width: 100%;
  min-height: 88px;
  box-sizing: border-box;
  padding: ${SPACE[8]} ${SPACE[12]};
  border-radius: 12px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const EmbedError = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.4;
  color: ${STATUS_COLORS.negative};
`;

/* ── Live preview controls ── */

/** An interactive preview: the frame, then its controls. */
export const PreviewFrameWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACE[8]};
  max-width: 100%;
`;

export const PreviewControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
`;

/** Where a CTA or T&Cs press in the preview would go in the live ad. */
export const PreviewEventNote = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
  text-align: center;
  overflow-wrap: anywhere;
`;

/* ── The rail's own pieces (inside the ingredient sections) ── */

/** A mini-game's Change of promotion, under the subject card's end (pulled up to sit close to the card). */
export const SubjectAction = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -${SPACE[8]};
`;

/** A mini-game's locked game text under its offer: each line's name and the line, small and read-only. */
export const GameText = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: baseline;
  gap: ${SPACE[4]} ${SPACE[12]};
  margin-top: ${SPACE[8]};
  font-size: 12px;
  line-height: 1.45;
`;

export const GameTextLabel = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.textFaint};
  white-space: nowrap;
`;

export const GameTextValue = styled(Text).attrs({ tag: 'span' as const })`
  color: ${COLORS.textMuted};
  overflow-wrap: anywhere;
`;

/** The template section's Lumio fill, under the chosen template. */
export const FillRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

/* ── HTML5 banners: every size as a card, like the Image Banners size grid ── */

export const SizesWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/** Replay, above the cards at the end. */
export const SizesHead = styled.div`
  display: flex;
  justify-content: flex-end;
`;

/** The sizes as a regular grid of cards at least `$minW` wide (a wide strip spans two, see SizeCard); a two-cell
 *  card that does not fit a row's end leaves a hole that a later single card fills. */
export const SizeCardGrid = styled.div<{ $minW: number }>`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${(p) => p.$minW}px, 1fr));
  grid-auto-flow: dense;
  gap: ${SPACE[16]};
`;

/** The stage a size plays on, centred: one height for all, so the grid reads as rows. */
export const SizeStage = styled.div<{ $h: number }>`
  height: ${(p) => p.$h}px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACE[8]};
`;

/** A size's box while its document is built (or when this environment cannot build it). */
export const SizePlaceholder = styled.div<{ $w: number; $h: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(p) => Math.round(p.$w)}px;
  height: ${(p) => Math.round(p.$h)}px;
  max-width: 100%;
  padding: ${SPACE[8]};
  box-sizing: border-box;
  border-radius: 8px;
  background: ${COLORS.surfaceInset};
  text-align: center;
  overflow: hidden;
`;
